import { QueryClientProvider } from '@tanstack/react-query'
import { createRouter, RouterProvider } from '@tanstack/react-router'
import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'

import { DensityProvider } from '@/components/density-provider'
import { ThemeProvider } from '@/components/theme-provider'
import { env } from '@/config/env'
import { client } from '@/client/client.gen'
import { stampErrorStatus } from '@/lib/errorStatus'
import { clearOnIdentityChange, createQueryClient } from '@/lib/query-client'
import { configureAuth, configureConsent } from '@ventawiz/web-kit'
import { routeTree } from './routeTree.gen'
// Side-effect imports: i18next must be initialised before the first component
// calls useTranslation, or every string renders as its own key.
import '@/i18n'
import '@/styles/globals.css'

// Before the router mounts: the `_app` guard calls `restore()` in its
// `beforeLoad`, which is the very first thing that touches the auth service.
// `application`: every front end shares one sign-in session, which holds one
// application at a time. Claiming ours makes the kit switch the session to
// VentaWiz when someone arrives from another app (User Management), so one
// sign-in opens every app they have.
configureAuth({ baseUrl: env.authBaseUrl, application: 'ventawiz' })
// The cookie-consent decision, shared with the other VentaWiz apps.
configureConsent({ cookieDomain: env.consentCookieDomain })

// Before any query runs: the retry rule reads the status this puts on errors.
stampErrorStatus(client)

const queryClient = createQueryClient()
// A tenant switch, another person, or a sign-out — here or in another tab —
// leaves nothing cached for the identity that is gone.
clearOnIdentityChange(queryClient)

const router = createRouter({
  routeTree,
  // Handed to routes through context rather than imported by them, so a route
  // can be rendered in a test against a throwaway cache.
  context: { queryClient },
  defaultPreload: 'intent',
  scrollRestoration: true,
})

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}

const container = document.getElementById('root')
if (!container) throw new Error('No #root element — index.html and main.tsx disagree.')

createRoot(container).render(
  <StrictMode>
    <ThemeProvider>
      <DensityProvider>
        <QueryClientProvider client={queryClient}>
          <RouterProvider router={router} />
        </QueryClientProvider>
      </DensityProvider>
    </ThemeProvider>
  </StrictMode>,
)
